import {
  CollectionUrlHandleString,
  ObjectIdAsHexString,
} from "kreate-common/modules/business-types";
import { getResourceKey$GetCollection } from "kreate-server/commands/GetCollection/fetcher";
import { handler$GetCollection } from "kreate-server/commands/GetCollection/handler";
import { GetCollection$Result } from "kreate-server/commands/GetCollection/typing";
import { getResourceKey$GetUser } from "kreate-server/commands/GetUser/fetcher";
import { handler$GetUser } from "kreate-server/commands/GetUser/handler";
import { GetUser$Result } from "kreate-server/commands/GetUser/typing";
import { getResourceKey$LoginStatus } from "kreate-server/commands/LoginStatus/fetcher";
import { handler$LoginStatus } from "kreate-server/commands/LoginStatus/handler";
import { LoginStatus$Result } from "kreate-server/commands/LoginStatus/typing";
import { Db } from "mongodb";
import { GetServerSideProps } from "next";
import { SWRConfig } from "swr";

import PageCollection from "../../../containers/PageCollection";
import {
  intentionallyIgnoreError,
  omitUndefinedRecursively,
} from "../../../utils";

import { getDb } from "@/modules/mongodb";
import { FallbackField, formatFallbackObject } from "@/modules/ssr/utils";

type Props = {
  collectionId: ObjectIdAsHexString;
  fallback: {
    loginStatus: FallbackField<LoginStatus$Result>;
    getCollection: FallbackField<GetCollection$Result>;
    getUser: FallbackField<GetUser$Result>;
  };
};

export default function Route({ collectionId, fallback }: Props) {
  return (
    <SWRConfig value={{ fallback: formatFallbackObject(fallback) }}>
      <PageCollection collectionId={collectionId} />
    </SWRConfig>
  );
}

async function resolveToCollectionId(
  db: Db,
  collectionIsh: string
): Promise<ObjectIdAsHexString | undefined> {
  const collectionIdSP = ObjectIdAsHexString.safeParse(collectionIsh);
  if (collectionIdSP.success) {
    return collectionIdSP.data;
  }

  const collectionHandleSP = CollectionUrlHandleString.safeParse(collectionIsh);
  if (collectionHandleSP.success) {
    const collection = await handler$GetCollection(db, {
      handle: collectionHandleSP.data,
    });
    return collection.collectionId;
  }

  return undefined;
}

export const getServerSideProps: GetServerSideProps<Props> = async (
  context
) => {
  const collectionIsh = context.query?.collectionIsh;
  if (typeof collectionIsh !== "string") return { notFound: true };

  const db = await getDb();
  const collectionId = await resolveToCollectionId(db, collectionIsh).catch(
    intentionallyIgnoreError
  );
  if (!collectionId) return { notFound: true };

  const collection = await handler$GetCollection(db, {
    collectionId,
  }).catch(intentionallyIgnoreError);

  const fallback: Props["fallback"] = {
    loginStatus: {
      resourceKey: getResourceKey$LoginStatus(),
      result: await handler$LoginStatus(db, {
        token: context.req.cookies["token"],
      }).catch(intentionallyIgnoreError),
    },
    getCollection: {
      resourceKey: getResourceKey$GetCollection({ collectionId }),
      result: collection,
    },
    getUser: {
      resourceKey: collection
        ? getResourceKey$GetUser({ userId: collection.createdBy })
        : undefined,
      result: await handler$GetUser(db, {
        userId: collection?.createdBy,
      }).catch(intentionallyIgnoreError),
    },
  };

  // if collection id is used while there is collection handle,
  // redirect to the link using collection handle
  if (fallback.getCollection.result) {
    const collection = fallback.getCollection.result;
    if (collectionIsh === collection.collectionId && !!collection.handle) {
      return {
        redirect: {
          destination: `/collection/${collection.handle}`,
          permanent: false,
        },
      };
    }
  }

  return {
    props: omitUndefinedRecursively({
      collectionId,
      fallback,
    }),
  };
};
